import format from "date-fns/format";
import set from "date-fns/set";
import { Formik, FormikProps } from "formik";
import { FC, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../../../components/atoms/Icon";
import {
  ForbiddenError,
  UnprocessableEntityError,
} from "../../../../../errors";
import TimeHelper from "../../../../../helpers/TimeHelper";
import { useFlashMessage } from "../../../../../hooks/useFlashMessage";
import { ApiErrorInterface } from "../../../../../interfaces/ApiErrorResponseInterface";
import { StayFormInterface } from "../../../../../interfaces/StayInterface";
import { HTTP_ERROR_MESSAGE } from "../../../../../reducers";
import { SectionStay } from "../../../useFetchSectionStays";
import { useDeleteSectionStay } from "../useDeleteSectionStay";
import { useUpdateSectionStay } from "../useUpdateSectionStay";
import { EnterLeaveTimeInfo } from "./EnterLeaveTimeInfo";
import styles from "./styles.scss";
import { UpdateModal } from "./UpdateModal";

interface Props {
  stay: SectionStay;
  deleteSectionStayMutation: ReturnType<typeof useDeleteSectionStay>;
  updateSectionStayMutation: ReturnType<typeof useUpdateSectionStay>;
}

interface Values {
  date: Date;
  enteredAt: string;
  exitedAt: string;
}

const SectionStayItem: FC<Props> = ({
  stay,
  updateSectionStayMutation,
  deleteSectionStayMutation,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [apiErrors, setApiErrors] = useState<ApiErrorInterface[]>([]);

  const { showSuccessMessage, showErrorMessage } = useFlashMessage();
  const isSubmitting =
    updateSectionStayMutation.isPending || deleteSectionStayMutation.isPending;

  const initialValues: Values = {
    date: new Date(stay.enteredAt),
    enteredAt: format(new Date(stay.enteredAt), "HH:mm"),
    exitedAt: stay.exitedAt ? format(new Date(stay.exitedAt), "HH:mm") : "",
  };

  const handleUpdateModalOpen = () => {
    setIsOpen(true);
  };

  const handleUpdateModalClose = () => {
    setIsOpen(false);
  };

  const handleDeleteStay = () => {
    if (isSubmitting) {
      return;
    }

    if (window.confirm("この入退室履歴を削除しますか？")) {
      deleteSectionStayMutation.mutate(stay.id, {
        onSuccess: () => {
          showSuccessMessage("入退室記録を削除しました");
        },
        onError: () => {
          showErrorMessage(HTTP_ERROR_MESSAGE);
        },
      });
    }
  };

  const handleUpdateSectionStay = (values: Values) => {
    if (isSubmitting) {
      return;
    }

    updateSectionStayMutation.mutate(
      {
        stayId: stay.id,
        params: mapValuesToParams(values),
      },
      {
        onSuccess: () => {
          showSuccessMessage("入退室記録を更新しました");
          handleUpdateModalClose();
          setApiErrors([]);
        },
        onError: (error) => {
          if (
            error instanceof ForbiddenError ||
            error instanceof UnprocessableEntityError
          ) {
            showErrorMessage("入退室記録を更新できませんでした");
          } else {
            showErrorMessage(HTTP_ERROR_MESSAGE);
          }

          if (error instanceof UnprocessableEntityError) {
            setApiErrors(error.originalErrors);
          }
        },
      },
    );
  };

  return (
    <tr key={`sectionStay-${stay.id}`} className={styles.row}>
      <td>
        <Link
          to={`/students/${stay.student.id}`}
          className={styles.studentLink}
        >
          {stay.student.fullName}
        </Link>
      </td>
      <td>{stay.student.jobHuman}</td>
      <td>{stay.enteredSection?.sectionName}</td>
      <td>
        <EnterLeaveTimeInfo
          time={stay.enteredAt}
          buildingName={stay.enteredBuildingName}
          status={stay.enteredStatus}
        />
      </td>
      <td>
        {stay.isExited && stay.exitedAt && (
          <EnterLeaveTimeInfo
            time={stay.exitedAt}
            buildingName={stay.exitedBuildingName}
            status={stay.exitedStatus}
          />
        )}
      </td>
      <td>
        {stay.isExited && stay.staySeconds
          ? TimeHelper.secondsToDisplayTime(stay.staySeconds)
          : "ー"}
      </td>
      <td className={styles.last_col}>
        <button className={styles.link} onClick={handleUpdateModalOpen}>
          <Icon name="icon-edit" />
          編集
        </button>
        <button className={styles.link} onClick={handleDeleteStay}>
          <Icon name="icon-trash" />
          削除
        </button>
      </td>
      <Formik
        initialValues={initialValues}
        initialErrors={{}}
        enableReinitialize
        onSubmit={handleUpdateSectionStay}
      >
        {(formikProps: FormikProps<Values>) => (
          <UpdateModal
            isOpen={isOpen}
            onRequestClose={() => {
              handleUpdateModalClose();
              formikProps.resetForm();
            }}
            stay={stay}
            isSubmitting={isSubmitting}
            apiErrors={apiErrors}
          />
        )}
      </Formik>
    </tr>
  );
};

const mapValuesToParams = (values: Values): StayFormInterface => {
  const enteredAt = values.enteredAt.split(":");
  const exitedAt = values.exitedAt.split(":");
  return {
    entered_at: set(values.date, {
      hours: parseInt(enteredAt[0]),
      minutes: parseInt(enteredAt[1]),
    }).toISOString(),
    exited_at: values.exitedAt
      ? set(values.date, {
          hours: parseInt(exitedAt[0]),
          minutes: parseInt(exitedAt[1]),
        }).toISOString()
      : "",
  };
};

export default SectionStayItem;
